// One place for Europe/London date handling. The backend speaks UTC (see
// CLAUDE.md "Time-zone contract"); the UI shows the user's local day, which for
// this product is always Europe/London. A day string like "2026-04-24" is a
// London day, never a UTC one, so never derive it with toISOString().

export const TIME_ZONE = 'Europe/London';

const dayFormatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
});

// "YYYY-MM-DD" of the London day containing `when`.
export function londonDay(when = new Date()) {
    return dayFormatter.format(new Date(when));
}

export const localToday = (now = new Date()) => londonDay(now);

// Add `n` days to a "YYYY-MM-DD" string. Pure calendar arithmetic, so DST
// changes can't push it onto the wrong day.
export function shiftLocalDay(dateString, n) {
    const [y, m, d] = dateString.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

// London's UTC offset in ms at instant `ms`.
function londonOffsetMs(ms) {
    const parts = {};
    new Intl.DateTimeFormat('en-GB', {
        timeZone: TIME_ZONE, hourCycle: 'h23',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).formatToParts(new Date(ms)).forEach(p => { parts[p.type] = p.value; });
    const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
    return asUtc - Math.floor(ms / 1000) * 1000;
}

// UTC ms of London midnight starting `dateString`.
function londonMidnight(dateString) {
    const [y, m, d] = dateString.split('-').map(Number);
    const guess = Date.UTC(y, m - 1, d);
    return guess - londonOffsetMs(guess - londonOffsetMs(guess));
}

// [start, end) of a London day as UTC ms. 23 or 25 hours long on DST days.
export function localDayBounds(dateString) {
    return { start: londonMidnight(dateString), end: londonMidnight(shiftLocalDay(dateString, 1)) };
}

// Backend files are UTC days, so a response can spill past local midnight.
// Keep only records (each with a UTC `time`) inside the selected London day.
export function filterToLocalDay(records, dateString) {
    if (!records || !dateString) return records || [];
    const { start, end } = localDayBounds(dateString);
    return records.filter(r => {
        const t = new Date(r.time).getTime();
        return t >= start && t < end;
    });
}

const PRESETS = {
    time: { hour: '2-digit', minute: '2-digit' },
    weekdayTime: { weekday: 'short', hour: '2-digit', minute: '2-digit' },
    weekdayDateTime: { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' },
    dateTime: { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' },
    full: { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' },
};
const formatters = {};

// Format a UTC instant for display in London time. Returns fallback for empty input.
export function formatLondon(when, preset = 'dateTime', fallback = '-') {
    if (when == null || when === '') return fallback;
    if (!formatters[preset]) {
        formatters[preset] = new Intl.DateTimeFormat('en-GB', { timeZone: TIME_ZONE, hourCycle: 'h23', ...PRESETS[preset] });
    }
    return formatters[preset].format(new Date(when));
}
